import axios from "axios";

// ALWAYS use relative URLs ("/api/...") so requests go to the same domain
// the app is served from. This works in both preview and production.
// DO NOT use process.env.REACT_APP_BACKEND_URL — the deployment platform
// injects a different domain that causes cross-origin issues.
const api = axios.create({
  baseURL: "/api",
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Auth
export const authAPI = {
  register: (data) => api.post("/auth/register", data),
  login: (data) => api.post("/auth/login", data),
  logout: () => api.post("/auth/logout"),
  me: () => api.get("/auth/me"),
  refresh: () => api.post("/auth/refresh"),
  completeOnboarding: () => api.post("/auth/complete-onboarding"),
};

// Verdict
export const verdictAPI = {
  analyze: (imageBase64Array) =>
    api.post("/verdict/analyze", {
      images: Array.isArray(imageBase64Array)
        ? imageBase64Array
        : [imageBase64Array],
    }),
  rescan: (verdictId) => api.post(`/verdict/${verdictId}/rescan`),
  correct: (verdictId, message) => api.post(`/verdict/${verdictId}/correct`, { message }),
  history: (limit = 50, skip = 0) =>
    api.get(`/verdict/history?limit=${limit}&skip=${skip}`),
  get: (id) => api.get(`/verdict/${id}`),
  delete: (id) => api.delete(`/verdict/${id}`),
  search: (q) => api.get(`/verdict/search/items?q=${encodeURIComponent(q)}`),
  setHaul: (verdictId, haulId) =>
    api.patch(`/verdict/${verdictId}/haul`, { haul_id: haulId }),
  analysisStatus: (verdictId) => api.get(`/verdict/${verdictId}/analysis`),
  retryMarket: (verdictId) => api.post(`/verdict/${verdictId}/retry-market`),
};

// Quick-list: photograph → upload → AI verdict → eBay listing.
// Photos eagerly uploaded to storage as the user picks them. "List Item"
// fires an async job; the client polls for status so we don't trip the
// 60-100s Cloudflare ingress timeout on slow market-research tiers (iter_44).
export const quicklistAPI = {
  upload: (file, onUploadProgress) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/quicklist/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 90000,
      onUploadProgress,
    });
  },
  analyze: (photo_paths, custom_label = "") =>
    api.post("/quicklist/analyze", { photo_paths, custom_label }, { timeout: 30000 }),
  job: (job_id) => api.get(`/quicklist/jobs/${job_id}`, { timeout: 30000 }),
};

// Hauls
export const haulAPI = {
  create: (data) => api.post("/hauls/create", data),
  list: () => api.get("/hauls/list"),
  get: (id) => api.get(`/hauls/${id}`),
  update: (id, data) => api.patch(`/hauls/${id}`, data),
  delete: (id) => api.delete(`/hauls/${id}`),
  reassign: (data) => api.post("/hauls/reassign", data),
  merge: (data) => api.post("/hauls/merge", data),
  scanReceipt: (data) => api.post("/hauls/receipt", data),
  deleteReceipt: (haulId, receiptId) => api.delete(`/hauls/${haulId}/receipt/${receiptId}`),
  stats: () => api.get("/hauls/stats"),
};

// Images
export const imageAPI = {
  convert: (image_base64) => api.post("/images/convert", { image_base64 }),
  upload: (images, verdict_id) => api.post("/images/upload", { images, verdict_id }),
  getPhotos: (verdictId) => api.get(`/images/photos/${verdictId}`),
};

// eBay
export const ebayAPI = {
  env: () => api.get("/ebay/env"),
  authStatus: () => api.get("/ebay/auth/status"),
  connect: () => api.get("/ebay/auth/connect"),
  disconnect: () => api.post("/ebay/auth/disconnect"),
  policies: () => api.get("/ebay/policies"),
  listingForm: (verdictId, edit = false) => api.get(`/ebay/listing-form/${verdictId}${edit ? "?edit=true" : ""}`),
  categorySuggest: (q) => api.get(`/ebay/category-suggest?q=${encodeURIComponent(q)}`),
  aspects: (categoryId, title, brand, condition) =>
    api.get(`/ebay/aspects/${categoryId}?title=${encodeURIComponent(title || "")}&brand=${encodeURIComponent(brand || "")}&condition=${encodeURIComponent(condition || "")}`),
  list: (data) => api.post("/ebay/list", data),
  retryPublish: (verdictId) => api.post(`/ebay/retry-publish/${verdictId}`),
  update: (data) => api.put("/ebay/update", data),
  listingData: (verdictId) => api.get(`/ebay/listing-data/${verdictId}`),
  // Campaigns
  campaigns: () => api.get("/ebay/campaigns"),
  createCampaign: (data) => api.post("/ebay/campaigns", data),
  updateCampaign: (id, data) => api.patch(`/ebay/campaigns/${id}`, data),
  deleteCampaign: (id) => api.delete(`/ebay/campaigns/${id}`),
  pauseCampaign: (id) => api.post(`/ebay/campaigns/${id}/pause`),
  resumeCampaign: (id) => api.post(`/ebay/campaigns/${id}/resume`),
  setDefaultCampaign: (id) => api.post("/ebay/campaigns/set-default", { campaign_id: id }),
  addToCampaign: (campaignId, listingId) => api.post(`/ebay/campaigns/${campaignId}/add-listing`, { listing_id: listingId }),
  suggestCampaigns: () => api.post("/ebay/campaigns/suggest-defaults"),
  // Payouts → Ledger
  payoutsPreview: () => api.get("/ebay/payouts/preview"),
  payoutsSync: () => api.post("/ebay/payouts/sync"),
  payoutsSyncFull: () => api.post("/ebay/payouts/sync?force_full=1"),
  payoutsImportById: (payoutId) => api.post(`/ebay/payouts/import-by-id/${encodeURIComponent(payoutId)}`),
  payoutsDiscover: (payoutIds = "", days = 90) =>
    api.get("/ebay/payouts/discover", { params: { payout_ids: payoutIds, days } }),
  explainError: (payload) => api.post("/ebay/explain-error", payload),
  // Size compliance
  sizeScan: () => api.post("/ebay/compliance/scan"),
  sizeFix: (fixes) => api.post("/ebay/compliance/fix", { fixes }),
};

// Preferences
export const prefsAPI = {
  get: () => api.get("/preferences"),
  update: (data) => api.put("/preferences", data),
  reset: () => api.post("/preferences/reset", { scope: "all" }),
  resetBrandRulesAndPrices: () => api.post("/preferences/reset", { scope: "brand_rules_and_prices" }),
  defaults: () => api.get("/preferences/defaults"),
};

// Sales / P&L
export const salesAPI = {
  sync: () => api.post("/sales/sync"),
  list: (limit = 50, skip = 0, haulId = "") =>
    api.get(`/sales/list?limit=${limit}&skip=${skip}${haulId ? `&haul_id=${haulId}` : ""}`),
  summary: (period = "all") => api.get(`/sales/summary?period=${period}`),
  haulPnl: (haulId) => api.get(`/sales/haul/${haulId}`),
  syncStatus: () => api.get("/sales/sync-status"),
  addManual: (data) => api.post("/sales/manual", data),
};

// Expenses
export const expenseAPI = {
  scan: (image_base64) => api.post("/expenses/scan", { image_base64 }),
  bulkScan: (images) => api.post("/expenses/bulk-scan", { images }),
  save: (data) => api.post("/expenses", data),
  list: (limit = 100, skip = 0, category = "", haulId = "") =>
    api.get(`/expenses?limit=${limit}&skip=${skip}${category ? `&category=${category}` : ""}${haulId ? `&haul_id=${haulId}` : ""}`),
  categories: () => api.get("/expenses/categories"),
  update: (id, data) => api.patch(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`),
};




// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    // Only try to refresh if it's not already a refresh request and not the initial auth check
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url.includes('/auth/refresh') && !originalRequest.url.includes('/auth/me')) {
      originalRequest._retry = true;
      try {
        await authAPI.refresh();
        return api(originalRequest);
      } catch {
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export const adminAPI = {
  usageSummary: (days = 7) => api.get(`/admin/usage/summary?days=${days}`),
  usageByUser: (days = 30, limit = 25) =>
    api.get(`/admin/usage/by-user?days=${days}&limit=${limit}`),
  usageTimeline: (days = 14) => api.get(`/admin/usage/timeline?days=${days}`),
  usageRecent: (limit = 100) => api.get(`/admin/usage/recent?limit=${limit}`),
  soldDataBreakdown: (days = 7) =>
    api.get(`/admin/usage/sold-data-breakdown?days=${days}`),
  scraperRecent: (limit = 100) =>
    api.get(`/admin/usage/scraper-recent?limit=${limit}`),
};

// Personal ledger / accounting tracker
export const ledgerAPI = {
  listTransactions: (params = {}) =>
    api.get("/ledger/transactions", { params }),
  createTransaction: (payload) => api.post("/ledger/transactions", payload),
  updateTransaction: (id, payload) =>
    api.put(`/ledger/transactions/${id}`, payload),
  deleteTransaction: (id) => api.delete(`/ledger/transactions/${id}`),
  balance: () => api.get("/ledger/balance"),
  summary: (params = {}) => api.get("/ledger/summary", { params }),
  listCategories: () => api.get("/ledger/categories"),
  createCategory: (name) => api.post("/ledger/categories", { name }),
  deleteCategory: (id) => api.delete(`/ledger/categories/${id}`),
  exportCsvUrl: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== "" && v != null)
    ).toString();
    return `/api/ledger/export.csv${qs ? "?" + qs : ""}`;
  },
};


export default api;
